import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Flyout, FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { toastInfo } from "@app/exports/export-panel-body/toast/ReportQueuedToast";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import SharedUsers from "@app/reports/SharedUsers";
import ExportService from "@app/exports/exportService";
import { ExportProviderHubReportModel } from "@app/exports/exportModels";
import {ExportFormatType, MembershipType} from "@app/exports/enumerations";
import { toast } from "react-toastify";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { ProviderHubFilters, ProviderSearchByEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ColumnConfigurationSection } from "@app/report-customization/build-reports/components/ColumnConfigurationSection";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { reportTypeToColumnsEndpointMap } from "@app/report-customization/build-reports/helpers/helpers";
import { ExportTypeEnum } from "@app/operations/exportTypeEnum";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";

interface ProviderHubGenerateReportFlyoutProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  filters: ProviderHubFilters;
  // Optional DOM id for the flyout panel. Set when the flyout is opened over another flyout/drawer so its
  // stacking (and backdrop) can be elevated above the underlying panel via CSS (see the IAP drawer report).
  panelId?: string;
}

const defaultPreset: ColumnConfigurationPresetOption = { id: -1, isPrivate: false, name: "Default" };

const ProviderHubGenerateReportFlyoutContent: FC<ProviderHubGenerateReportFlyoutProps> = ({ isModalOpen, setIsModalOpen, filters, panelId }) => {
  const flyoutContext = useContext(FlyoutContext);
  const [exportName, setExportName] = useState<string>(helpers.reportName("ProviderHubReport"));
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [sharedUsers, setSharedUsers] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [presets, setPresets] = useState<ColumnConfigurationPresetOption[]>([defaultPreset]);
  const [selectedPreset, setSelectedPreset] = useState<ColumnConfigurationPresetOption>(defaultPreset);

  const exportService = useMemo(() => new ExportService(), []);
  const prevIsModalOpen = useRef(false);

  useEffect(() => {
    exportService.getColumnCustomizationPresets(ReportType.ProviderHubServicingProviderReport)
      .then((apiPresets) => setPresets([defaultPreset, ...apiPresets]))
      .catch(() => {});
  }, [exportService]);

  useEffect(() => {
    if (isModalOpen && !prevIsModalOpen.current) {
      flyoutContext.handleOpen();
    }

    prevIsModalOpen.current = isModalOpen;
  }, [isModalOpen, flyoutContext]);

  useEffect(() => {
    if (flyoutContext.hasOpened && !flyoutContext.isFlyoutVisible) {
      setIsModalOpen(false);
    }
  }, [flyoutContext.isFlyoutVisible, flyoutContext.hasOpened, setIsModalOpen]);

  const handlePresetsRefresh = async () => {
    const apiPresets = await exportService.getColumnCustomizationPresets(ReportType.ProviderHubServicingProviderReport);
    setPresets([defaultPreset, ...apiPresets]);
  };

  const resetFormValues = () => {
    setExportName(helpers.reportName("ProviderHubReport"));
    setEmailOnSuccess(true);
    setSharedUsers([]);
  };

  const exportModel = useMemo((): ExportProviderHubReportModel => ({
    accessPointCountMax: filters.accessPointCountMax,
    accessPointCountMin: filters.accessPointCountMin,
    careJourneyCostScore: (filters.searchByOption === ProviderSearchByEnum.ContractEntity)? Array.from({ length: Math.round((filters.careJourneyCostScoreMax - filters.careJourneyCostScoreMin) / 0.1) + 1}, (_, index) => +((index * 0.1) + filters.careJourneyCostScoreMin).toFixed(1)) : filters.careJourneyCostScore,
    careJourneyQualityScore: (filters.searchByOption === ProviderSearchByEnum.ContractEntity)? Array.from({ length: Math.round((filters.careJourneyQualityScoreMax - filters.careJourneyQualityScoreMin) / 0.1) + 1}, (_, index) => +((index * 0.1) + filters.careJourneyQualityScoreMin).toFixed(1)) : filters.careJourneyQualityScore,
    commercialClaimsVolume: filters.commercialClaimsVolume.map(s => s.key),
    commercialNewPatientIndicator: filters.commercialNewPatientIndicator.map(s => s.key),
    effectiveness: (filters.searchByOption === ProviderSearchByEnum.ContractEntity) ? Array.from({ length: Math.round((filters.effectivenessMax - filters.effectivenessMin) / 0.1) + 1 }, (_, index) => +((index * 0.1) + filters.effectivenessMin).toFixed(1)) : filters.effectiveness,
    efficiency: (filters.searchByOption === ProviderSearchByEnum.ContractEntity) ? Array.from({ length: Math.round((filters.efficiencyMax - filters.efficiencyMin) / 0.1) + 1 }, (_, index) => +((index * 0.1) + filters.efficiencyMin).toFixed(1)) : filters.efficiency,
    eMailOnSuccess: emailOnSuccess,
    exportName,
    formatType: ExportFormatType.CSV,
    gapClosedMax: filters.closeGapsMax,
    gapClosedMin: filters.closeGapsMin,
    includeUnknownCareJourneyCostScore: filters.includeUnknownCareJourneyCostScore,
    includeUnknownCareJourneyQualityScore: filters.includeUnknownCareJourneyQualityScore,
    includeUnknownEffectiveness: filters.includeUnknownEffectiveness,
    includeUnknownEfficiency: filters.includeUnknownEfficiency,
    includeUnknownMotiveScore: filters.includeUnknownMotiveScore,
    locationConfidence: filters.locationConfidence.map(s => s.key),
    medicaidClaimsVolume: filters.medicaidClaimsVolume.map(s => s.key),
    medicaidNewPatientIndicator: filters.medicaidNewPatientIndicator.map(s => s.key),
    medicareClaimsVolume: filters.medicareClaimsVolume.map(s => s.key),
    medicareNewPatientIndicator: filters.medicareNewPatientIndicator.map(s => s.key),
    motiveScore: (filters.searchByOption === ProviderSearchByEnum.ContractEntity)? Array.from({ length: Math.round((filters.motiveScoreMax - filters.motiveScoreMin) / 0.1) + 1}, (_, index) => +((index * 0.1) + filters.motiveScoreMin).toFixed(1)) : filters.motiveScore,
    networkIntegrityAttestationBasedAlerts: filters.networkIntegrityAttestationBasedAlerts,
    networkIntegrityCriticalAlerts: filters.networkIntegrityCriticalAlerts,
    networkIntegrityExcessAddressesMax: filters.networkIntegrityExcessAddressesMax,
    networkIntegrityExcessAddressesMin: filters.networkIntegrityExcessAddressesMin,
    networkIntegrityExcessSpecialtiesMax: filters.networkIntegrityExcessSpecialtiesMax,
    networkIntegrityExcessSpecialtiesMin: filters.networkIntegrityExcessSpecialtiesMin,
    networkIntegrityProjectOptionsExclusions: filters.networkIntegrityProjectOptionsExclusions,
    projectId: filters.projectId,
    projectRunConfigurationId: filters.membershipType === MembershipType.Primary
      ? filters.projectRunConfigurationId
      : filters.companionProjectRunConfigurationId,
    providerActionStatuses: filters.providerActionStatuses,
    providerNetworkTypes: filters.providerNetworkTypes,
    runAsynchronous: true,
    searchByInput: filters.searchByInput,
    searchByOption: filters.searchByOption,
    serviceAreas: filters.regionSetIds,
    sharedUsers,
    specialtyGroupCodes: filters.specialtyGroupCodes.map(s => s.key),
    totalClaimsVolume: filters.totalClaimsVolume.map(s => s.key),
    totalNewPatientIndicator: filters.totalNewPatientIndicator.map(s => s.key),
    tricareClaimsVolume: filters.tricareClaimsVolume.map(s => s.key),
    tricareNewPatientIndicator: filters.tricareNewPatientIndicator.map(s => s.key),
    vaClaimsVolume: filters.vaClaimsVolume.map(s => s.key),
    vaNewPatientIndicator: filters.vaNewPatientIndicator.map(s => s.key),
    viewByOption: filters.viewByOption,
  }), [filters, exportName, emailOnSuccess, sharedUsers]);

  const generateProviderHubReport = async () => {
    setIsLoading(true);
    const model: ExportProviderHubReportModel = {
      ...exportModel,
      reportCustomizationId: selectedPreset.id !== -1 ? selectedPreset.id : undefined
    };
    try {
      await exportService.postExportProviderHubReport(model);
      toastInfo(exportName);
      resetFormValues();
      setIsModalOpen(false);
      await flyoutContext.handleCloseFlyout();
    } catch (error) {
      toast.error('Error generating Provider Hub report', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isGenerateDisabled = !exportName || isLoading;

  return (
    <Flyout
      content={{
        body: (
          <>
            <div className="ps-3 pe-3 mb-3">
              <QESTextInput
                testId="provider-hub__generate-file-modal-input"
                label="File Name"
                required={true}
                value={exportName}
                isValid={!!exportName}
                onChange={(e) => setExportName((e.target as HTMLInputElement).value)}
              />
              <div className="mt-2" style={{ fontSize: "0.95em" }}>
                Note: Created reports will be downloaded in .csv format.
              </div>
            </div>
            <div className="ps-3 pe-3 mb-3">
              <SharedUsers
                exportType={capitalizeFirstLetter(ExportTypeEnum.ProviderHubServicingProviderReport)}
                customProjectIds={filters.projectId ? [filters.projectId] : []}
                tooltipTitle="Users in this list have been filtered to only show users with access to this project and information in this report"
                updateSelectedSharedUsers={setSharedUsers}
                sharedUsersFilter={sharedUsers}
                containerClassnames="d-block"
              />
            </div>
            <div className="ps-3 pe-3 mb-3">
              <ColumnConfigurationSection
                columnsEndpoint={reportTypeToColumnsEndpointMap.get(ReportType.ProviderHubServicingProviderReport)}
                exportModel={exportModel}
                presets={presets}
                selectedPreset={selectedPreset}
                reportType={ReportType.ProviderHubServicingProviderReport}
                onPresetChange={setSelectedPreset}
                onPresetsRefresh={handlePresetsRefresh}
              />
            </div>
            <div className="ps-3 pe-3 mb-3">
              <QESCheckbox
                label="Email me a link to the report once it's ready."
                checked={emailOnSuccess}
                onChange={(e) => setEmailOnSuccess((e.target as HTMLInputElement).checked)}
                id="provider-hub-generate-report-modal__email-me"
                testId="provider-hub__generate-report-modal__email-me"
              />
            </div>
            <FlyoutFooter
              submitButton={
                <QESButton
                  qesType="primary"
                  disabled={isGenerateDisabled}
                  onClick={generateProviderHubReport}
                  qesTestId="provider-hub__generate-report-flyout__generate"
                >
                  {isLoading ? "Generating..." : "Generate Report"}
                </QESButton>
              }
            />
          </>
        ),
        title: "Provider Report",
      }}
      panelId={panelId}
      size="40"
    />
  );
};

const ProviderHubGenerateReportFlyout: FC<ProviderHubGenerateReportFlyoutProps> = (props) => {
  return (
    <FlyoutContextProvider>
      <ProviderHubGenerateReportFlyoutContent {...props} />
    </FlyoutContextProvider>
  );
};

export default ProviderHubGenerateReportFlyout;
